/**
 * Storage Cleanup Utility
 *
 * Removes deprecated localStorage keys that are no longer used after React Query migration.
 * This should be run once on app initialization to clean up user's localStorage.
 */

import { logger } from './logger';

/**
 * List of deprecated localStorage keys that should be removed.
 * These keys have been migrated to React Query persistence.
 */
const DEPRECATED_KEYS = [
  // Legacy theme key (replaced by themePreference)
  'darkMode',

  // Old Better Auth session keys (Better Auth handles its own storage now)
  'better-auth.session',
  'better-auth.session.token',
  'better-auth.session.expiresAt',

  // Old cache keys that are now handled by React Query
  'club_cache_timestamp',
  'club_', // Prefix for old club caches
  'clubs_list_cache_timestamp',

  // Any other legacy keys you want to remove
  // Note: Current keys like 'themePreference', 'locale', 'selected_organization_id' are still in use
  // and will NOT be removed (they're now managed by React Query but use same key names)
] as const;

/**
 * Keys that should be removed based on prefix matching
 */
const DEPRECATED_PREFIXES = [
  'club_', // Old club cache entries (e.g., club_abc123)
  'clubs_list_', // Old clubs list cache
  'permissions_', // Old permissions cache (now handled server-side with better caching)
] as const;

/**
 * Check if a key should be removed based on exact match or prefix
 */
function shouldRemoveKey(key: string): boolean {
  // Check exact matches
  if (DEPRECATED_KEYS.includes(key as (typeof DEPRECATED_KEYS)[number])) {
    return true;
  }

  // Check prefix matches
  return DEPRECATED_PREFIXES.some((prefix) => key.startsWith(prefix));
}

/**
 * Clean up deprecated localStorage keys.
 * This is safe to run multiple times - it will only remove deprecated keys.
 *
 * @returns Object with removed keys and count
 */
export function cleanupDeprecatedStorage(): { removed: string[]; count: number } {
  if (typeof window === 'undefined') {
    return { removed: [], count: 0 };
  }

  const removed: string[] = [];

  try {
    // Get all localStorage keys
    const allKeys = Object.keys(localStorage);

    logger.log('[Storage Cleanup] Checking', allKeys.length, 'localStorage keys...');

    // Check each key
    for (const key of allKeys) {
      if (shouldRemoveKey(key)) {
        try {
          localStorage.removeItem(key);
          removed.push(key);
          logger.log('[Storage Cleanup] Removed deprecated key:', key);
        } catch (error) {
          logger.warn('[Storage Cleanup] Failed to remove key:', key, error);
        }
      }
    }

    if (removed.length > 0) {
      logger.log('[Storage Cleanup] ✓ Removed', removed.length, 'deprecated keys');
    } else {
      logger.log('[Storage Cleanup] ✓ No deprecated keys found');
    }

    // Mark cleanup as completed (so we don't run it again unnecessarily)
    try {
      localStorage.setItem('storage_cleanup_v1_completed', 'true');
    } catch (error) {
      logger.warn('[Storage Cleanup] Failed to mark cleanup as completed:', error);
    }

    return { removed, count: removed.length };
  } catch (error) {
    logger.error('[Storage Cleanup] Error during cleanup:', error);
    return { removed, count: removed.length };
  }
}

/**
 * Check if storage cleanup has already been run.
 * This prevents running cleanup on every app load.
 */
export function hasCleanupRun(): boolean {
  if (typeof window === 'undefined') {
    return true; // SSR - skip cleanup
  }

  try {
    return localStorage.getItem('storage_cleanup_v1_completed') === 'true';
  } catch {
    return false;
  }
}

/**
 * Run storage cleanup if it hasn't been run yet.
 * Safe to call on every app initialization.
 */
export function runCleanupIfNeeded(): void {
  if (hasCleanupRun()) {
    logger.log('[Storage Cleanup] Already completed, skipping...');
    return;
  }

  logger.log('[Storage Cleanup] Running first-time cleanup...');
  cleanupDeprecatedStorage();
}
